import {useEffect, useState, useContext, JSX} from 'react';
import { Chip, List, ListItem, ListItemText, Typography, IconButton } from '@mui/material';
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { AlertSnippetDto } from '../../../interfaces/IEvent';
import { AlertSendStatus, AlertSendType } from '../../../interfaces/IAlert';
import { toDateTime } from '../../../utils/ValueFormatters';
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework';
import { LoadingIndicator } from '@eidsivabredband/react-component-ui-package';

interface AlertSnippetProps {
    alertSnippet: AlertSnippetDto;
    eventId: string;
    onDelete?: (alertId: string) => void;
    onEdit?: (alertId: string) => void;
    onView?: (alertId: string) => void;
    onUpdate?: (eventId: string) => void;
}

function AlertSnippet({ alertSnippet, eventId, onDelete, onEdit, onView, onUpdate }: AlertSnippetProps) {
    const [confirmDeleteButton, setConfirmDeleteButtom] = useState<boolean>(false);
    const [alertTypeMessage, setAlertTypeMessage] = useState<JSX.Element | string>();
    const {context} = useContext(EbAuthenticatedAppContext);
    const [currentStatus, setCurrentStatus] = useState<AlertSendStatus>(alertSnippet.status as AlertSendStatus);

    const AlertTypeMessages = {
        IsSent: `Sendt ${toDateTime(new Date(alertSnippet.sendOn as Date))}`,
        IsSentPartially: `Delvis/ikke sendt ${toDateTime(new Date(alertSnippet.sendOn as Date))}`,
        NotProcessed: `Utsendelse ikke startet enda`,
        Processing: `Sender...`,
        FailedBeforeSend: `Feilet før sending`,
        IsScheduled: `Sendes ${toDateTime(new Date(alertSnippet.sendOn))}`,
        IsImmediate: `Umiddelbar sending (venter...)`,
        IsDraft: `Utkast – ikke sendt`,
    };

    const getAlertStatus = async () => {
        const status = await context.restApiClient
            .buildRequest(`/api/event/${eventId}/alert/${alertSnippet.id}/status`)
            .getAsync();

        if(status.response?.content){
            return setCurrentStatus(status.response.content as AlertSendStatus);
        }
    };

    const handleAlertClick = () => {
        // Processing is transitional and shows a spinner instead of buttons; everything else can be viewed.
        if (currentStatus !== AlertSendStatus.Processing) {
            return (
                handleViewAlert()
            )
        }
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete(alertSnippet.id);
            setConfirmDeleteButtom(false);
        }
    };

    const handleEdit = () => {
        if (onEdit) {
            onEdit(alertSnippet.id);
        }
    };

    const handleViewAlert = () => {
        if (onView) {
            onView(alertSnippet.id);
        }
    };

    const viewButton = (
        <IconButton size="small" onClick={handleViewAlert}>
            <ChevronRightOutlinedIcon />
        </IconButton>
    );

    const alertButtons =
        ((currentStatus === AlertSendStatus.FailedBeforeSend ||
            currentStatus === AlertSendStatus.Sent ||
            currentStatus === AlertSendStatus.SentPartially) &&
            viewButton) ||
        // Drafts always show view, edit and delete buttons
        (alertSnippet.status === AlertSendStatus.Draft &&
            <>
                {viewButton}
                <IconButton size="small" onClick={handleEdit}>
                    <EditOutlinedIcon color="warning" />
                </IconButton>
                {confirmDeleteButton &&
                    <IconButton size="small" onClick={handleDelete}>
                        <CheckOutlinedIcon color="success" />
                    </IconButton>
                }
                <IconButton size="small" onClick={() => setConfirmDeleteButtom(!confirmDeleteButton)}>
                    {confirmDeleteButton ? <CloseOutlinedIcon color='error' /> : <DeleteOutlinedIcon color="error" />}
                </IconButton>
            </>) ||
        (alertSnippet.type === AlertSendType.Scheduled &&
            <>
                {viewButton}
                <IconButton
                    size="small"
                    onClick={handleEdit}
                >
                    <EditOutlinedIcon color="warning" />
                </IconButton>
                {confirmDeleteButton &&
                    <IconButton
                        size="small"
                        onClick={handleDelete}
                    >
                        <CheckOutlinedIcon color="success" />
                    </IconButton>
                }
                <IconButton
                    size="small"
                    onClick={() => setConfirmDeleteButtom(!confirmDeleteButton)}
                >
                    {confirmDeleteButton ?
                        <CloseOutlinedIcon color='error' /> : <DeleteOutlinedIcon color="error" />
                    }
                </IconButton>
            </>
        )
    ;

    const fetchAlertTypeMessage = (status: AlertSendStatus) => {
        if (status === AlertSendStatus.Draft) {
            return AlertTypeMessages.IsDraft;
        }
        if (status === AlertSendStatus.Sent) {
            return AlertTypeMessages.IsSent;
        }
        if (status === AlertSendStatus.SentPartially) {
            return AlertTypeMessages.IsSentPartially;
        }
        if (status === AlertSendStatus.NotProcessed) {
            if (alertSnippet.type === AlertSendType.Scheduled && alertSnippet.sendOn) {
                return new Date(alertSnippet.sendOn as Date) <= new Date()
                    ? AlertTypeMessages.Processing
                    : AlertTypeMessages.IsScheduled;
            }
            else if (alertSnippet.type === AlertSendType.Immediate) {
                return AlertTypeMessages.IsImmediate;
            }
            else {
                return AlertTypeMessages.NotProcessed;
            }
        }
        if (status === AlertSendStatus.Processing) {
            return AlertTypeMessages.Processing;
        }
        if (status === AlertSendStatus.FailedBeforeSend) {
            return AlertTypeMessages.FailedBeforeSend;
        }
    }

    useEffect(() => {
        setCurrentStatus(alertSnippet.status as AlertSendStatus);
    }, [alertSnippet.status]);

    useEffect(() => {
        if(alertSnippet){
            setAlertTypeMessage(fetchAlertTypeMessage(currentStatus));

            if (currentStatus === AlertSendStatus.NotProcessed || currentStatus === AlertSendStatus.Processing) {
                const intervalId = setInterval(() => {
                    getAlertStatus();
                }, 3000);

                return () => clearInterval(intervalId);
            }
        }
    }, [currentStatus]);

    return (
        <List disablePadding>
            <ListItem
                disablePadding
                disableGutters
                sx={{
                    cursor: 'pointer',
                    backgroundColor: "background.paper",
                    "&:hover": {
                        backgroundColor: 'action.hover',
                    },
                }}
            >
                <ListItemText
                    primary={
                        <>
                            {alertSnippet.description}
                            {alertSnippet.status === AlertSendStatus.Draft && (
                                <Chip
                                    label="UTKAST"
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 1, fontSize: '0.65rem', height: 18 }}
                                />
                            )}
                        </>
                    }
                    secondary={<Typography variant="body2">{alertTypeMessage}</Typography>}
                    onClick={handleAlertClick}
                    slotProps={{
                        primary: {
                            component: 'span' as const,
                            variant: 'body2',
                            sx: { fontWeight: 600, whiteSpace: 'normal' }
                        }
                    }}
                />
                {fetchAlertTypeMessage(currentStatus) !== AlertTypeMessages.Processing ?
                    <ListItemText
                        sx={{
                            textAlign: 'right',
                        }}
                        secondary={alertButtons}
                    />
                    :
                    <ListItemText
                        sx={{
                            textAlign: 'right',
                            display: 'contents',
                        }}
                        secondary={<LoadingIndicator size={'small'} />}
                    />
                }
            </ListItem>
        </List>
    );
}

export default AlertSnippet;